import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Heart, ArrowLeft, Loader2, Quote, MessageCircle, Send, Trash2 } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { getVisitorId } from "../lib/utils";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function ageFromYear(y) {
  if (!y) return "";
  const now = new Date().getFullYear();
  return `${now - parseInt(y) + 1}세`;
}

// 커플 발표일 포맷: "2026-06-06" → "2026.6.6"
function fmtCoupleDate(s) {
  if (!s) return "";
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}.${parseInt(m[2], 10)}.${parseInt(m[3], 10)}`;
  return s;
}

export default function CoupleStoriesPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [couples, setCouples] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/liz-couples`)
      .then((r) => (r.ok ? r.json() : { couples: [] }))
      .then((d) => {
        const list = (d.couples || []).filter((c) => c.is_visible !== false);
        // 최근 등록순 (created_at desc)
        list.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        setCouples(list);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const selected = id ? couples.find((c) => c.id === id) : couples[0];
  const others = couples.filter((c) => c.id !== selected?.id);

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 pt-24 flex justify-center">
        <Loader2 className="w-7 h-7 animate-spin text-rose-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 pt-20" data-testid="couple-stories-page">
      {/* Header */}
      <div className="bg-stone-100 border-b border-stone-200 py-10">
        <div className="max-w-4xl mx-auto px-6">
          <button
            onClick={() => navigate("/community")}
            className="flex items-center gap-1.5 text-stone-400 hover:text-stone-600 text-sm mb-4 transition-colors"
            data-testid="couple-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> 커뮤니티
          </button>
          <div className="flex items-center gap-3">
            <Heart className="w-7 h-7 text-burgundy-500 fill-burgundy-500" />
            <div>
              <h1 className="text-3xl font-bold text-stone-900">우리 커플됐어요</h1>
              <p className="text-stone-500 mt-1">New Coupling에서 만난 두 사람의 진짜 이야기</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {!selected ? (
          <div className="text-center py-20">
            <Heart className="w-12 h-12 text-stone-300 mx-auto mb-4" />
            <p className="text-stone-400">아직 등록된 커플 이야기가 없습니다.</p>
          </div>
        ) : (
          <motion.div
            key={selected.id}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-3xl border border-stone-200 overflow-hidden shadow-sm"
            data-testid="couple-story-detail"
          >
            {/* Hero photo */}
            <div className="relative aspect-[16/9] bg-gradient-to-br from-stone-200 to-stone-300">
              {selected.photo_url ? (
                <img src={selected.photo_url} alt={`${selected.couple_no}호 커플`} className="w-full h-full object-cover" style={{ objectPosition: selected.photo_position === "top" ? "center top" : selected.photo_position === "bottom" ? "center bottom" : "center" }} />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Heart className="w-16 h-16 text-stone-400 fill-stone-300" />
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
              <span className="absolute top-4 left-4 bg-burgundy-700 text-white text-sm font-bold px-3 py-1 rounded-full shadow">
                {selected.couple_no}호
              </span>
              {selected.announced_date && (
                <span className="absolute top-4 right-4 bg-white/90 text-burgundy-700 text-xs font-bold px-3 py-1 rounded-full shadow" data-testid="couple-detail-date">
                  {fmtCoupleDate(selected.announced_date)} 커플 탄생
                </span>
              )}
              <div className="absolute bottom-4 left-6 right-6">
                <h2 className="text-white text-2xl font-bold drop-shadow">{selected.couple_name || `${selected.couple_no}호 커플`}</h2>
                {selected.message && <p className="text-white/90 text-sm mt-1 italic drop-shadow">"{selected.message}"</p>}
              </div>
            </div>

            {/* Stories */}
            <div className="p-6 sm:p-8 grid md:grid-cols-2 gap-5">
              {/* Male */}
              <div className="bg-sky-50 rounded-2xl p-5 border border-sky-100" data-testid="couple-male-story">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-bold text-sky-600 bg-sky-100 px-2 py-0.5 rounded-full">남성</span>
                  <span className="text-sm font-semibold text-stone-700">
                    {selected.male_name ? `${selected.male_name}OO` : "남성"}
                    {selected.male_location ? ` · ${selected.male_location}` : ""}
                    {selected.male_birth_year ? ` · ${ageFromYear(selected.male_birth_year)}` : ""}
                  </span>
                </div>
                <Quote className="w-4 h-4 text-sky-300 mb-1" />
                <p className="text-[14px] text-stone-700 leading-relaxed whitespace-pre-wrap">
                  {selected.male_story || "아직 작성된 이야기가 없어요."}
                </p>
              </div>
              {/* Female */}
              <div className="bg-pink-50 rounded-2xl p-5 border border-pink-100" data-testid="couple-female-story">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-bold text-pink-600 bg-pink-100 px-2 py-0.5 rounded-full">여성</span>
                  <span className="text-sm font-semibold text-stone-700">
                    {selected.female_name ? `${selected.female_name}OO` : "여성"}
                    {selected.female_location ? ` · ${selected.female_location}` : ""}
                    {selected.female_birth_year ? ` · ${ageFromYear(selected.female_birth_year)}` : ""}
                  </span>
                </div>
                <Quote className="w-4 h-4 text-pink-300 mb-1" />
                <p className="text-[14px] text-stone-700 leading-relaxed whitespace-pre-wrap">
                  {selected.female_story || "아직 작성된 이야기가 없어요."}
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* 좋아요 / 댓글 */}
        {selected && <CoupleSocial coupleId={selected.id} />}

        {/* Other couples */}
        {others.length > 0 && (
          <div className="mt-10">
            <h3 className="text-lg font-bold text-stone-800 mb-4">다른 커플 이야기</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {others.map((c, idx) => (
                <motion.button
                  key={c.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.04 }}
                  onClick={() => navigate(`/couples/${c.id}`)}
                  className="bg-white rounded-2xl border border-stone-200 overflow-hidden hover:shadow-lg hover:border-burgundy-300 transition-all text-left group"
                  data-testid={`other-couple-${c.couple_no}`}
                >
                  <div className="relative aspect-square bg-stone-100">
                    {c.photo_url ? (
                      <img src={c.photo_url} alt={`${c.couple_no}호`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" style={{ objectPosition: c.photo_position === "top" ? "center top" : c.photo_position === "bottom" ? "center bottom" : "center" }} />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-stone-100 to-stone-200">
                        <Heart className="w-8 h-8 text-stone-300" />
                      </div>
                    )}
                    <span className="absolute top-2 left-2 bg-burgundy-700 text-white text-[11px] font-bold px-2 py-0.5 rounded-full">{c.couple_no}호</span>
                    {c.announced_date && (
                      <span className="absolute top-2 right-2 bg-white/90 text-burgundy-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow">{fmtCoupleDate(c.announced_date)}</span>
                    )}
                  </div>
                  <div className="p-2.5">
                    <p className="text-sm font-bold text-stone-800 truncate">{c.couple_name || `${c.couple_no}호 커플`}</p>
                  </div>
                </motion.button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "방금 전";
  if (diff < 3600) return `${Math.floor(diff / 60)}분 전`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}시간 전`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}일 전`;
  return new Date(iso).toLocaleDateString("ko-KR");
}

function CoupleSocial({ coupleId }) {
  const { user, token } = useAuth();
  const [likes, setLikes] = useState(0);
  const [liked, setLiked] = useState(false);
  const [liking, setLiking] = useState(false);
  const [comments, setComments] = useState([]);
  const [content, setContent] = useState("");
  const [nickname, setNickname] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  const load = () => {
    const vid = getVisitorId();
    fetch(`${API_URL}/api/liz-couples/${coupleId}/social?visitor_id=${encodeURIComponent(vid)}`, { headers: authHeader })
      .then((r) => (r.ok ? r.json() : null))
      .then((s) => {
        if (s) { setLikes(s.likes || 0); setLiked(!!s.liked); setComments(s.comments || []); }
      })
      .catch(() => {});
  };

  useEffect(() => { if (coupleId) load(); /* eslint-disable-next-line */ }, [coupleId, token]);

  const toggleLike = async () => {
    if (liking) return;
    setLiking(true);
    try {
      const r = await fetch(`${API_URL}/api/liz-couples/${coupleId}/like`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader },
        body: JSON.stringify({ visitor_id: getVisitorId() }),
      });
      if (r.ok) { const d = await r.json(); setLikes(d.likes); setLiked(d.liked); }
    } catch { /* noop */ }
    setLiking(false);
  };

  const submitComment = async () => {
    const text = content.trim();
    if (!text) { toast.error("댓글 내용을 입력해 주세요."); return; }
    if (!user && !nickname.trim()) { toast.error("닉네임을 입력해 주세요."); return; }
    setSubmitting(true);
    try {
      const r = await fetch(`${API_URL}/api/liz-couples/${coupleId}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader },
        body: JSON.stringify({ content: text, author_name: nickname.trim() }),
      });
      if (r.ok) {
        const d = await r.json();
        setComments((prev) => [...prev, d.comment]);
        setContent("");
        toast.success("댓글이 등록되었습니다.");
      } else {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || "댓글 등록에 실패했습니다.");
      }
    } catch { toast.error("댓글 등록에 실패했습니다."); }
    setSubmitting(false);
  };

  const deleteComment = async (id) => {
    try {
      const r = await fetch(`${API_URL}/api/liz-couples/${coupleId}/comments/${id}`, {
        method: "DELETE",
        headers: authHeader,
      });
      if (r.ok) { setComments((prev) => prev.filter((c) => c.id !== id)); toast.success("삭제되었습니다."); }
      else { toast.error("삭제 권한이 없습니다."); }
    } catch { toast.error("삭제에 실패했습니다."); }
  };

  return (
    <div className="mt-6 bg-white rounded-3xl border border-stone-200 shadow-sm p-6 sm:p-8" data-testid="couple-social">
      {/* 좋아요 */}
      <div className="flex items-center gap-4 pb-5 border-b border-stone-100">
        <button
          onClick={toggleLike}
          disabled={liking}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm transition-all ${liked ? "bg-rose-500 text-white shadow-sm" : "bg-stone-100 text-stone-600 hover:bg-rose-50 hover:text-rose-500"}`}
          data-testid="couple-like-btn"
        >
          <Heart className={`w-4 h-4 ${liked ? "fill-white" : ""}`} />
          축하해요 <span data-testid="couple-like-count">{likes}</span>
        </button>
        <div className="flex items-center gap-1.5 text-stone-400 text-sm font-semibold">
          <MessageCircle className="w-4 h-4" />
          댓글 <span data-testid="couple-comment-count">{comments.length}</span>
        </div>
      </div>

      {/* 댓글 목록 */}
      <div className="py-5 space-y-4" data-testid="couple-comment-list">
        {comments.length === 0 ? (
          <p className="text-center text-stone-400 text-sm py-6">첫 번째로 축하 메시지를 남겨보세요 💐</p>
        ) : (
          comments.map((c) => (
            <div key={c.id} className="flex gap-3 group" data-testid={`couple-comment-${c.id}`}>
              <div className="w-9 h-9 rounded-full bg-burgundy-100 text-burgundy-700 flex items-center justify-center font-bold text-sm shrink-0">
                {(c.author || "익")[0]}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-stone-800">{c.author || "익명"}</span>
                  {c.is_member && <span className="text-[10px] font-bold text-burgundy-600 bg-burgundy-50 px-1.5 py-0.5 rounded-full">회원</span>}
                  <span className="text-xs text-stone-400">{timeAgo(c.created_at)}</span>
                </div>
                <p className="text-[14px] text-stone-700 mt-0.5 whitespace-pre-wrap break-words">{c.content}</p>
              </div>
              {(user?.role === "admin" || (user && c.author_id === user.id)) && (
                <button
                  onClick={() => deleteComment(c.id)}
                  className="opacity-0 group-hover:opacity-100 text-stone-300 hover:text-rose-500 transition-all shrink-0"
                  data-testid={`couple-comment-delete-${c.id}`}
                  aria-label="댓글 삭제"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))
        )}
      </div>

      {/* 댓글 작성 */}
      <div className="pt-4 border-t border-stone-100 space-y-2" data-testid="couple-comment-form">
        {!user && (
          <input
            type="text"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            placeholder="닉네임"
            maxLength={20}
            className="w-full px-3.5 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-burgundy-400"
            data-testid="couple-comment-nickname"
          />
        )}
        <div className="flex gap-2">
          <input
            type="text"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.nativeEvent.isComposing) submitComment(); }}
            placeholder="축하 메시지를 남겨주세요"
            maxLength={1000}
            className="flex-1 px-3.5 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-burgundy-400"
            data-testid="couple-comment-input"
          />
          <button
            onClick={submitComment}
            disabled={submitting}
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-burgundy-600 hover:bg-burgundy-700 text-white text-sm font-bold disabled:opacity-50 transition-colors shrink-0"
            data-testid="couple-comment-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            등록
          </button>
        </div>
      </div>
    </div>
  );
}
